import { RateLimitError } from "@atsready/shared/utils";
import type { BlogCmsTicketRequest } from "@/services/blog-cms";

/**
 * Pure helpers for the C2 ticket body and the C3 frame messages. No React here, and the only
 * runtime import is `@atsready/shared/utils`, so `AuthContext → session-logout → protocol` cannot
 * cycle back to `api-client`.
 */

/**
 * D4's six tokens plus `allow-top-navigation-by-user-activation` (T32), which lets S2's
 * `target="_top"` "Return to admin" link work inside the frame on a user click. Bare
 * `allow-top-navigation` stays out.
 */
export const BLOG_CMS_IFRAME_SANDBOX =
  "allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads allow-top-navigation-by-user-activation";

/** Cold load: the editor bundle is about 2 MB (RV6). Spec M1-4 said 15 s; the triage amended it. */
export const BLOG_CMS_READY_TIMEOUT_MS = 30_000;
/** After an editor re-ticket, when the bundle is cached (M1-4). */
export const BLOG_CMS_REAUTH_READY_TIMEOUT_MS = 15_000;
/** Renew C5 (1 h) every 45 min, with 15 min to spare (U1). */
export const BLOG_CMS_RENEW_INTERVAL_MS = 2_700_000;
/** Hidden renewal frame: ticket to `renewed`. */
export const BLOG_CMS_RENEW_TIMEOUT_MS = 15_000;
/** The one retry of a failed timer renewal (plan §7 A8). */
export const BLOG_CMS_RENEW_RETRY_MS = 60_000;
/** The C2 `next` literal for the hidden renewal frame (C6 `/_sso/renewed`). */
export const BLOG_CMS_RENEW_NEXT = "/_sso/renewed";
/** C2 `next` rule for editor paths. */
export const BLOG_CMS_NEXT_PATTERN = /^\/_emdash\/[A-Za-z0-9/_.-]*$/;
export const BLOG_CMS_NEXT_MAX = 512;

export type BlogCmsFrameMessage =
  | { type: "blog-cms:ready" }
  | { type: "blog-cms:reauth"; next: string; inPlace: boolean }
  | { type: "blog-cms:renewed" };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null) return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Reads one C3 message. Null unless the origin and the source both match and the expected source
 * is non-null. Which frame may send which type is the hook's rule, not this function's.
 */
export function readBlogCmsFrameMessage(
  event: MessageEvent,
  expected: { origin: string; source: MessageEventSource | null },
): BlogCmsFrameMessage | null {
  if (expected.source === null) return null;
  if (event.origin !== expected.origin || event.source !== expected.source) return null;

  const data: unknown = event.data;
  if (!isPlainObject(data)) return null;

  switch (data.type) {
    case "blog-cms:ready":
      return { type: "blog-cms:ready" };
    case "blog-cms:renewed":
      return { type: "blog-cms:renewed" };
    case "blog-cms:reauth": {
      if (typeof data.next !== "string") return null;
      if ("inPlace" in data && data.inPlace !== true) return null;
      return { type: "blog-cms:reauth", next: data.next, inPlace: data.inPlace === true };
    }
    default:
      return null;
  }
}

function isSafeEditorNext(next: string): boolean {
  if (next.length > BLOG_CMS_NEXT_MAX) return false;
  if (!BLOG_CMS_NEXT_PATTERN.test(next)) return false;
  return next.split("/").every((segment) => segment !== "." && segment !== "..");
}

/**
 * The editor re-ticket body. `{ mode:'human', next }` when `next` is a safe editor path (C2), else
 * `{}` (the editor home). It never emits `/_sso/renewed`: only `renewTicketRequest` does, so a frame
 * cannot steer the editor to the renewal page.
 */
export function reauthTicketRequest(next: string): BlogCmsTicketRequest {
  return isSafeEditorNext(next) ? { mode: "human", next } : {};
}

/** The hidden renewal frame's body (U1). */
export function renewTicketRequest(): BlogCmsTicketRequest {
  return { mode: "human", next: BLOG_CMS_RENEW_NEXT };
}

/** A2: admin learns the editor origin only from the C2 url. */
export function frameOriginOf(url: string): string {
  return new URL(url).origin;
}

export type TicketFailure =
  | { reason: "rate-limited"; retryAfterSeconds: number }
  | { reason: "failed" };

/** A missing `Retry-After` counts down from C2's 60 s rate window. */
export function classifyTicketFailure(error: unknown): TicketFailure {
  if (error instanceof RateLimitError) {
    return { reason: "rate-limited", retryAfterSeconds: error.retryAfter ?? 60 };
  }
  return { reason: "failed" };
}
